package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.TableError
import fr.manyverse.app.domain.model.ActiveTimer
import fr.manyverse.app.domain.repository.TimerRepository
import fr.manyverse.app.infrastructure.error.logInfrastructureFailure
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock
import kotlin.time.Instant

private const val TAG = "TimerRepository"
private const val TIMERS_TABLE = "game_table_timers"

class SupabaseTimerRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : TimerRepository {

    private val json = appJson {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    override suspend fun createTimer(
        tableId: String,
        durationSeconds: Int,
        targetParticipants: List<String>,
        createdBy: String
    ): Result<ActiveTimer> {
        return try {
            val insertDto = TimerInsertDto(
                tableId = tableId,
                durationSeconds = durationSeconds,
                targetParticipants = targetParticipants,
                createdBy = createdBy,
                isActive = true
            )

            val timer = supabaseClient
                .from(TIMERS_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<TimerDto>()

            Result.success(timer.toDomain())
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to create timer for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun getActiveTimers(
        tableId: String
    ): Result<List<ActiveTimer>> {
        return try {
            val timers = supabaseClient
                .from(TIMERS_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("table_id", tableId)
                        eq("is_active", true)
                    }
                }
                .decodeList<TimerDto>()

            Result.success(timers.map { it.toDomain() })
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to get active timers for table=$tableId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun stopTimer(
        timerId: String
    ): Result<Unit> {
        return try {
            supabaseClient
                .from(TIMERS_TABLE)
                .update({
                    set("is_active", false)
                }) {
                    filter {
                        eq("id", timerId)
                    }
                }

            Result.success(Unit)
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to stop timer=$timerId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override suspend fun pauseTimer(
        timerId: String
    ): Result<ActiveTimer> = updatePauseState("pause_timer", timerId)

    override suspend fun resumeTimer(
        timerId: String
    ): Result<ActiveTimer> = updatePauseState("resume_timer", timerId)

    /**
     * Pause/resume go through RPCs so that the server clock (consistent with created_at)
     * stamps the pause, instead of the GM device clock.
     */
    private suspend fun updatePauseState(
        function: String,
        timerId: String
    ): Result<ActiveTimer> {
        return try {
            val timer = supabaseClient.postgrest
                .rpc(function, buildJsonObject { put("p_timer_id", timerId) })
                .decodeList<TimerDto>()
                .firstOrNull()
                ?: return Result.failure(TableError("Timer not found or not active"))

            Result.success(timer.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            AppLogger.warn(TAG, "Failed to $function timer=$timerId")
            Result.failure(mapToDomainException(e, "table"))
        }
    }

    override fun observeActiveTimers(
        tableId: String,
        currentUserId: String
    ): Flow<List<ActiveTimer>> = channelFlow {
        val activeTimers = mutableMapOf<String, ActiveTimer>()

        // État complet : chargé à l'ouverture, puis rechargé après une coupure. En cas d'échec, les
        // timers déjà connus restent affichés
        suspend fun loadActiveTimers() {
            try {
                val timers = supabaseClient
                    .from(TIMERS_TABLE)
                    .select(Columns.ALL) {
                        filter {
                            eq("table_id", tableId)
                            eq("is_active", true)
                        }
                    }
                    .decodeList<TimerDto>()
                    .map { it.toDomain() }
                    .filter { it.targetParticipants.contains(currentUserId) }

                activeTimers.clear()
                timers.forEach { activeTimers[it.id] = it }
                send(activeTimers.values.toList())
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                logInfrastructureFailure(TAG, "Failed to load active timers for table=$tableId", e)
            }
        }

        gameChannel.events(TIMERS_TABLE).collect { event ->
            when (event) {
                GameChannelEvent.Ready -> loadActiveTimers()
                is GameChannelEvent.Change -> {
                    val update = event.change.record?.let { toTimerUpdate(it, tableId, currentUserId) }
                        ?: return@collect
                    if (update.timer != null) {
                        activeTimers[update.timerId] = update.timer
                    } else {
                        activeTimers.remove(update.timerId)
                    }
                    send(activeTimers.values.toList())
                }
            }
        }
    }

    /**
     * Effet d'une ligne insérée ou modifiée sur les timers affichés : ajout ou mise à jour d'un timer
     * actif, retrait d'un timer arrêté. `null` si la ligne ne concerne pas l'utilisateur.
     */
    private fun toTimerUpdate(
        record: Map<String, Any?>,
        tableId: String,
        currentUserId: String
    ): TimerUpdate? = try {
        val timerId = extractString(record["id"])
        val targetParticipants = extractStringList(json, record["target_participants"])
        when {
            timerId == null || extractString(record["table_id"]) != tableId -> null
            !targetParticipants.contains(currentUserId) -> null
            extractBoolean(record["is_active"]) != true -> TimerUpdate(timerId, null)
            else -> parseTimerFromRecord(record, tableId, currentUserId)?.let { TimerUpdate(it.id, it) }
        }
    } catch (e: Exception) {
        AppLogger.error(TAG, "Failed to parse timer from real-time update", e)
        null
    }

    private data class TimerUpdate(val timerId: String, val timer: ActiveTimer?)

    private fun parseTimerFromRecord(
        record: Map<String, Any?>,
        expectedTableId: String,
        currentUserId: String
    ): ActiveTimer? {
        val tableId = extractString(record["table_id"]) ?: return null

        if (tableId != expectedTableId) {
            return null
        }

        val isActive = extractBoolean(record["is_active"]) ?: false
        if (!isActive) {
            return null
        }

        val targetParticipants = extractStringList(json, record["target_participants"])
        if (!targetParticipants.contains(currentUserId)) {
            return null
        }

        val id = extractString(record["id"]) ?: return null
        val createdBy = extractString(record["created_by"]) ?: return null
        val durationSeconds = extractInt(record["duration_seconds"]) ?: return null
        val createdAtStr = extractString(record["created_at"]) ?: return null
        val createdAt = try {
            Instant.parse(createdAtStr)
        } catch (e: Exception) {
            Clock.System.now()
        }
        // JsonNull is a JsonPrimitive whose content is "null": filter it before parsing
        val pausedAt = record["paused_at"]
            ?.takeUnless { it is JsonNull }
            ?.let { extractString(it) }
            ?.let { Instant.parse(it) }
        val pausedSeconds = extractInt(record["paused_seconds"]) ?: 0

        return ActiveTimer(
            id = id,
            tableId = tableId,
            durationSeconds = durationSeconds,
            targetParticipants = targetParticipants,
            createdBy = createdBy,
            isActive = isActive,
            createdAt = createdAt,
            pausedAt = pausedAt,
            pausedSeconds = pausedSeconds
        )
    }


    @Serializable
    private data class TimerInsertDto(
        @SerialName("table_id")
        val tableId: String,

        @SerialName("duration_seconds")
        val durationSeconds: Int,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("is_active")
        val isActive: Boolean
    )

    @Serializable
    private data class TimerDto(
        @SerialName("id")
        val id: String,

        @SerialName("table_id")
        val tableId: String,

        @SerialName("duration_seconds")
        val durationSeconds: Int,

        @SerialName("target_participants")
        val targetParticipants: List<String>,

        @SerialName("created_by")
        val createdBy: String,

        @SerialName("is_active")
        val isActive: Boolean,

        @SerialName("created_at")
        val createdAt: Instant,

        @SerialName("paused_at")
        val pausedAt: Instant? = null,

        @SerialName("paused_seconds")
        val pausedSeconds: Int = 0
    ) {
        fun toDomain(): ActiveTimer = ActiveTimer(
            id = id,
            tableId = tableId,
            durationSeconds = durationSeconds,
            targetParticipants = targetParticipants,
            createdBy = createdBy,
            isActive = isActive,
            createdAt = createdAt,
            pausedAt = pausedAt,
            pausedSeconds = pausedSeconds
        )
    }
}
